import { Menu, Typography } from '@neo4j-ndl/react';
import { Square2StackIconOutline } from '@neo4j-ndl/react/icons';
import type { Connection } from '@nx/state';
import {
  consoleApi,
  useActiveDatabase,
  useCapability,
  useConnection,
  useConnectionSelectorModal,
  useConnections,
  useFeatureFlag,
} from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { skipToken } from '@reduxjs/toolkit/query';

import { useCopyWithMessage } from '../clipboard-copier/clipboard-copier';
import { ConnectionStatusIndicator } from './connection-status-indicator';

const RecentInstancesMenu = ({ onSelect }: { onSelect: (connection: Connection) => void }) => {
  const recentConnections = useConnections();

  return recentConnections.length > 0 ? (
    <Menu title="Recent instances">
      <Menu.Items>
        {recentConnections.map((connection) => (
          <Menu.Item
            title={connection.name}
            description={connection.url}
            key={connection.url}
            onClick={() => onSelect(connection)}
          />
        ))}
      </Menu.Items>
    </Menu>
  ) : null;
};

interface Props {
  isMenuOpen: boolean;
  onClose: () => void;
  anchorEl?: React.RefObject<HTMLElement | null>;
}

export function InstanceMenu({ isMenuOpen, anchorEl, onClose }: Props) {
  const connection = useConnection();
  const connections = useConnections();
  const connectionSelectorModal = useConnectionSelectorModal();
  const [, copyWithMessage] = useCopyWithMessage();
  const [remoteConnectionsEnabled] = useFeatureFlag('upx:remote-connections');
  const activeDatabase = useActiveDatabase();

  const auraInstanceId = connection.metadata?.auraId;
  const { data: currentInstance } = consoleApi.useGetInstanceQuery(
    connection.status.isConnected && isNotNullish(auraInstanceId) && auraInstanceId !== '' ? auraInstanceId : skipToken,
  );

  // Get the active connection URL from the connections list
  const activeConnection = connections.find(
    (conn) => connection.status.isConnected && conn.url === connection.metadata?.url,
  );

  const connectionURI: string | undefined = activeConnection?.url;

  const queryApiUrl = currentInstance?.queryApiUrl;

  const handleConnectionSelect = (conn: Connection) => {
    connectionSelectorModal.open({
      connectionDetails: { url: conn.url, credentials: conn.credentials },
    });
    onClose();
  };

  const handleCopyConnectionURI = () => {
    if (isNotNullish(connectionURI) && connectionURI !== '') {
      copyWithMessage(connectionURI);
    }
  };

  const handleCopyQueryApiUrl = () => {
    if (isNotNullish(queryApiUrl) && queryApiUrl !== '') {
      const url = isNotNullish(activeDatabase?.name)
        ? queryApiUrl.replace(/{databaseName}/g, activeDatabase.name)
        : queryApiUrl;
      copyWithMessage(url);
    }
  };

  const handleCopyInstanceId = () => {
    const auraId = connection.metadata?.auraId;
    if (connection.status.isConnected && isNotNullish(auraId) && auraId !== '') {
      copyWithMessage(auraId);
    }
  };

  const [showVersionCapability] = useCapability('framework:connection-bar-show-version');
  const showVersion = connection.status.isConnected && showVersionCapability;

  return (
    <Menu isOpen={isMenuOpen} anchorRef={anchorEl} onClose={() => onClose()}>
      <Menu.Items>
        <div className="ndl-menu-item ndl-disabled !cursor-default" role="menuitem" tabIndex={-1}>
          <Typography variant="body-medium" className="text-palette-neutral-text-weakest mr-1">
            Status:
          </Typography>
          <ConnectionStatusIndicator />
          <Typography variant="body-small" className="ml-1 capitalize">
            {connection.status.value}
          </Typography>
        </div>
        {showVersion && (
          <div className="ndl-menu-item ndl-disabled !cursor-default" role="menuitem" tabIndex={-1}>
            <Typography variant="body-medium" className="text-palette-neutral-text-weakest mr-1">
              Version:
            </Typography>
            <Typography variant="body-small" className="ml-1">
              {connection.versionAndEdition.version} ({connection.versionAndEdition.edition})
            </Typography>
          </div>
        )}
        {connection.status.isConnected && (
          <>
            {isNotNullish(connection.metadata?.auraId) && (
              <>
                <Menu.Divider />
                <Menu
                  title="Connection details"
                  isDisabled={
                    isNullish(connectionURI) && isNullish(queryApiUrl) && isNullish(connection.metadata.auraId)
                  }
                >
                  <Menu.Items>
                    {isNotNullish(connectionURI) && (
                      <Menu.Item
                        icon={<Square2StackIconOutline />}
                        title="Copy connection URI"
                        description={connectionURI}
                        onClick={() => handleCopyConnectionURI()}
                      />
                    )}
                    {isNotNullish(queryApiUrl) && (
                      <Menu.Item
                        icon={<Square2StackIconOutline />}
                        title="Copy Query API URL"
                        description={
                          <div className="max-w-72 truncate">
                            {isNotNullish(activeDatabase?.name)
                              ? queryApiUrl.replace(/{databaseName}/g, activeDatabase.name)
                              : queryApiUrl}
                          </div>
                        }
                        onClick={() => handleCopyQueryApiUrl()}
                      />
                    )}
                    {isNotNullish(connection.metadata.auraId) && (
                      <Menu.Item
                        icon={<Square2StackIconOutline />}
                        title="Copy Instance ID"
                        description={connection.metadata.auraId}
                        onClick={() => handleCopyInstanceId()}
                      />
                    )}
                  </Menu.Items>
                </Menu>
              </>
            )}
            <Menu.Divider />
            <Menu.Item
              title="Switch instance"
              onClick={() => {
                connectionSelectorModal.open();
                onClose();
              }}
            />
          </>
        )}

        {remoteConnectionsEnabled && <RecentInstancesMenu onSelect={handleConnectionSelect} />}

        {(connection.status.isConnected || connection.status.isConnecting || connection.status.isReconnecting) && (
          <Menu.Item
            title="Disconnect instance"
            onClick={() => {
              void connection.disconnect();
              onClose();
            }}
          />
        )}

        {connection.status.isDisconnected && (
          <Menu.Item
            title="Connect to instance"
            onClick={() => {
              connectionSelectorModal.open();
              onClose();
            }}
          />
        )}
      </Menu.Items>
    </Menu>
  );
}
